/* eslint-disable max-lines-per-function */
import classNames from 'classnames'
import { get } from 'lodash'
import { useRef, memo, useState, useEffect, useCallback } from 'react'
import { useRouter } from 'next/router'
import { Daum, columnList } from '../types/TCategory'
import LivePriceCommon from '../LivePrices/LivePriceCommon'
import Link from '@/components/Common/Link'
import { StencilCDNImage } from '@/components/Common/StencilImage'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/revamp/reusable-components/tooltip/tooltip'
import { formatNumberToINRCommaSeparatedString } from '@/root/utils/common/numbers/format-number-to-string'
import useHasMounted from '@/hooks/useHasMounted'
import { useAnalyticsEvent } from '@/hooks/analytics/use-analytics-event'
import { GA4Event } from '@/root/utils/common/web/gtm-events/types'

type Props = {
  type: string
  val: number | string
  socket_path?: string
}

type EventsData = {
  listClickEvent: GA4Event
}

function StockListingDynamicTable({
  columnList1,
  resultsList,
  hasNextPage,
  isFetchingNextPage,
  fetchNextPage,
  eventsData,
}: {
  columnList1?: columnList
  resultsList?: Daum[]

  isFetchingNextPage: boolean
  hasNextPage?: boolean
  fetchNextPage: () => void
  eventsData?: EventsData
}) {
  const router = useRouter()
  const hasMounted = useHasMounted()
  const pushAnalyticsEvent = useAnalyticsEvent()
  // To format raw dynamic column values received from BE based on key name
  const formatTableVal = ({ type, val, socket_path }: Props) => {
    let updatedVal: JSX.Element | null | string | number = <span>{val}</span>

    switch (type) {
      case 'livePrice':
        updatedVal = (
          <LivePriceCommon
            price={val}
            showPercentChange={false}
            layout="vertical"
            priceClassNames="text-right text-sm font-normal"
            socket_path={socket_path}
          />
        )

        break

      case 'percent':
        if (val && typeof val === 'number') {
          updatedVal = <span>{val}%</span>
        } else {
          updatedVal = <span>NA</span>
        }
        break

      case 'sentiment':
        if (typeof val === 'string') {
          updatedVal = (
            <span
              className={classNames({
                'text-brand-yellow-sunglow': val.toUpperCase() === 'HOLD',
                'text-brand-green-dark': val.toUpperCase() === 'BUY',
                'text-brand-red-cinnabar': val.toUpperCase() === 'SELL',
              })}
            >
              {val}
            </span>
          )
        }
        break

      case 'numberWithNA':
        if (typeof val === 'number') {
          if (val === 0) return 'NA'
          updatedVal = (
            <span>
              {hasMounted ? formatNumberToINRCommaSeparatedString(val, 0) : val}
            </span>
          )
        }
        break
      case 'priceWithCr':
        if (typeof val === 'number') {
          updatedVal = (
            <span>
              {hasMounted ? formatNumberToINRCommaSeparatedString(val, 0) : val}{' '}
              Cr
            </span>
          )
        }
        break

      case 'priceWithColor':
        if (typeof val === 'number') {
          updatedVal = (
            <span
              className={classNames({
                'text-brand-red-cinnabar': val < 0,
                'text-brand-green-dark': val > 0,
              })}
            >
              {hasMounted ? formatNumberToINRCommaSeparatedString(val) : val}
            </span>
          )
        }
        break

      case 'percentChangeColor':
        if (typeof val === 'number') {
          updatedVal = (
            <span
              className={classNames({
                'text-brand-red-cinnabar': val < 0,
                'text-brand-green-dark': val > 0,
              })}
            >
              {val}%
            </span>
          )
        }
        break

      default:
        break
    }
    return updatedVal
  }

  const rowHeaderRef = useRef<HTMLTableCellElement | null>(null)

  const [scrollIconVisible, setScrollIconVisible] = useState(true)

  const SCROLLABLE_EL_ID = 'listing-scroll-icon'

  // remove the scrollable animation element after animation
  useEffect(() => {
    const scrollableAnimationEl = document.getElementById(SCROLLABLE_EL_ID)

    const removeIsScrollableElAfterAnimation = (event: AnimationEvent) => {
      if (
        scrollableAnimationEl &&
        event.animationName === 'isScrollableIndicator'
      ) {
        scrollableAnimationEl.removeEventListener(
          'animationend',
          removeIsScrollableElAfterAnimation,
        )
        setScrollIconVisible(false)
      }
    }

    if (scrollableAnimationEl) {
      scrollableAnimationEl.addEventListener(
        'animationend',
        removeIsScrollableElAfterAnimation,
        { passive: true },
      )
    }

    return () => {
      if (scrollableAnimationEl) {
        scrollableAnimationEl.removeEventListener(
          'animationend',
          removeIsScrollableElAfterAnimation,
        )
      }
    }
  }, [])
  const onClickHandler = useCallback(({ stockLabel }) => {
    if (eventsData) {
      pushAnalyticsEvent({
        gtm4Payload: {
          eventData: { ...eventsData.listClickEvent, eventLabel: stockLabel },
        },
      })
    }
  }, [])

  if (!columnList1?.length || !resultsList?.length) {
    return null
  }

  return (
    <div className="my-3 flex flex-col">
      <div className="relative w-full overflow-x-auto ">
        <style jsx>{`
          @keyframes isScrollableIndicator {
            50% {
              transform: translateX(-100px);
            }

            100% {
              transform: translateX(0);
            }
          }

          #${SCROLLABLE_EL_ID} {
            animation: isScrollableIndicator 2s ease-in-out 0.5s;
          }
        `}</style>

        {scrollIconVisible && (
          <button
            className="absolute top-32 z-40 flex h-[20vh] w-full transform items-center justify-center bg-transparent"
            id={SCROLLABLE_EL_ID}
          >
            <div className="w-18 lg:w-25">
              <StencilCDNImage
                src="https://indcdn.indmoney.com/public/images/slider-2x.png"
                width={188}
                height={176}
                alt="slider-2x"
              />
            </div>
          </button>
        )}
        <table className="relative my-0 w-full table-auto border">
          <thead className="border-b">
            <tr className="relative">
              <th
                className="text-13px sticky -top-1 left-0 z-[8] min-w-[150px] max-w-fit break-words bg-white px-2 py-1 text-left font-normal md:min-w-[250px]"
                key={0}
              >
                Name
              </th>

              {columnList1.map(heading => {
                return (
                  <th
                    className="text-13px sticky -top-1 min-w-[90px] bg-white px-2 py-1 font-normal md:min-w-[120px]"
                    key={heading.key}
                    ref={rowHeaderRef}
                  >
                    <div className="flex items-center justify-end gap-2 break-words text-right">
                      <span>{heading.name}</span>
                      <div className="h-4 flex-shrink-0">
                        {heading.info && (
                          <Tooltip>
                            <TooltipTrigger>
                              <StencilCDNImage
                                src="https://indcdn.indmoney.com/public/images/info_new.png"
                                width={15}
                                height={15}
                                onClick={() => {}}
                                alt={`info about ${heading.name}`}
                              />
                            </TooltipTrigger>
                            <TooltipContent>
                              <div className="bg-brand-white border-1 shadow-one w-56 p-2 text-xs">
                                {heading.info}
                              </div>
                            </TooltipContent>
                          </Tooltip>
                        )}
                      </div>
                    </div>
                  </th>
                )
              })}
            </tr>
          </thead>
          <tbody>
            {resultsList.map(row => {
              return (
                <tr
                  key={row.name}
                  className="hover:bg-brand-grey-background group cursor-pointer border-b"
                  onClick={() => {
                    onClickHandler({ stockLabel: row.name })
                    if (row.relativepath) {
                      void router.push(row.relativepath)
                    }
                  }}
                >
                  <td
                    className="hover:text-brand-blue-azureRadiance sticky left-0 min-w-[150px] max-w-fit cursor-pointer break-words border-b px-2 py-2.5 text-sm transition-colors md:min-w-[250px]"
                    key={0}
                  >
                    <div className="group-hover:bg-brand-grey-background flex items-center gap-2 bg-white">
                      {row.logo && (
                        <StencilCDNImage
                          src={row.logo}
                          width={35}
                          height={35}
                          className="rounded-full"
                        />
                      )}
                      <Link href={row.relativepath}>{row.name} </Link>
                    </div>
                  </td>
                  {columnList1.map(columnObj => {
                    const key = columnObj.key

                    const type = columnObj.type ?? ''

                    const columnListVal: string | number = get(row, key)
                    return (
                      <td
                        className="min-w-max break-words border-b px-2 py-2.5 text-right text-sm md:min-w-[120px]"
                        key={columnObj.key}
                      >
                        {formatTableVal({
                          type,
                          val: columnListVal,
                          socket_path: row.socket_path,
                        })}
                      </td>
                    )
                  })}
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
      {hasNextPage && (
        <div className="flex justify-center">
          <button
            className="btn btn-medium mt-5 md:w-1/3"
            onClick={() => {
              void fetchNextPage()
            }}
          >
            {isFetchingNextPage ? 'Loading...' : 'View More'}
          </button>
        </div>
      )}
    </div>
  )
}

export default memo(StockListingDynamicTable)
